/**
 * Settings → Integrations. Every form is built from the provider catalogue returned by the API
 * (D-036): a provider added in the backend appears here without any front-end code.
 */

import {
  ApiError,
  type IntegrationConfig,
  type Provider,
  getIntegrationsConfigsListQueryKey,
  useIntegrationsConfigsCreate,
  useIntegrationsConfigsList,
  useIntegrationsConfigsMaintenanceCreate,
  useIntegrationsConfigsPartialUpdate,
  useIntegrationsConfigsTestCreate,
  useIntegrationsProvidersList,
} from "@agencyos/api-client";
import { Badge, Button, GlassCard, GlassSheet, Skeleton, TextField } from "@agencyos/ui-web";
import { useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";

import { SettingsLayout } from "../components/SettingsLayout";
import { useCurrentRole } from "../lib/settings";

/** D-132: the state of a payment provider (circuit breaker) and the switch « in maintenance ». */
function ProviderHealthRow({ config, onChanged }: { config: IntegrationConfig; onChanged: () => void }) {
  const { t, i18n } = useTranslation();
  const toggle = useIntegrationsConfigsMaintenanceCreate({ mutation: { onSuccess: onChanged } });
  const health = config.health;
  if (!health) return null;
  const locale = i18n.resolvedLanguage === "en" ? "en-GB" : "fr-FR";
  const when = (value: string | null) =>
    value ? new Date(value).toLocaleString(locale, { dateStyle: "short", timeStyle: "short", timeZone: "Africa/Douala" }) : "";
  const down = health.status === "maintenance";
  return (
    <div className="stack" role="status" aria-live="polite">
      <div className="row">
        <Badge tone={down ? "danger" : health.status === "degraded" ? "warning" : "success"}>
          {down
            ? health.forced
              ? t("integrations.health.forced")
              : t("integrations.health.maintenance")
            : health.status === "degraded"
              ? t("integrations.health.degraded", { count: health.failures })
              : t("integrations.health.ok")}
        </Badge>
        {down && health.until ? <span className="muted">{t("integrations.health.until", { time: when(health.until) })}</span> : null}
        {!down && health.recovered_at ? (
          <span className="muted">{t("integrations.health.recovered", { date: when(health.recovered_at) })}</span>
        ) : null}
      </div>
      <p className="muted">{t("integrations.health.hint")}</p>
      <div className="row">
        <Button
          variant={down ? "primary" : "secondary"}
          disabled={toggle.isPending}
          onClick={() => toggle.mutate({ id: config.id, data: { maintenance: !down } })}
        >
          {down ? t("integrations.health.restore") : t("integrations.health.force")}
        </Button>
      </div>
    </div>
  );
}

function ProviderCard({
  provider,
  config,
  isOwner,
}: {
  provider: Provider;
  config?: IntegrationConfig;
  isOwner: boolean;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: getIntegrationsConfigsListQueryKey() });
  const create = useIntegrationsConfigsCreate({ mutation: { onSuccess: refresh } });
  const update = useIntegrationsConfigsPartialUpdate({ mutation: { onSuccess: refresh } });
  const test = useIntegrationsConfigsTestCreate({ mutation: { onSuccess: refresh } });
  const [values, setValues] = useState<Record<string, string>>({});
  const [confirmLive, setConfirmLive] = useState(false);
  const settings = (config?.settings ?? {}) as Record<string, string>;
  const masked = (config?.masked_secrets ?? {}) as Record<string, string>;
  const saving = create.isPending || update.isPending;

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    const nextSettings: Record<string, string> = { ...settings };
    const secrets: Record<string, string> = {};
    for (const field of provider.fields) {
      const value = values[field.name]?.trim();
      if (!value) continue;
      if (field.secret) secrets[field.name] = value;
      else nextSettings[field.name] = value;
    }
    const data = { settings: nextSettings, ...(Object.keys(secrets).length ? { secrets } : {}) };
    const done = { onSuccess: () => setValues({}) };
    if (config) update.mutate({ id: config.id, data }, done);
    else create.mutate({ data: { category: provider.category, provider_key: provider.key, ...data } }, done);
  };

  return (
    <GlassCard as="article" className="stack">
      <div className="row between">
        <div>
          <h2>{provider.label}</h2>
          <div className="row">
            <Badge tone={config ? "success" : "default"}>
              {config ? t("integrations.configured") : t("integrations.notConfigured")}
            </Badge>
            {config ? (
              <Badge tone={config.mode === "live" ? "danger" : "offer"}>
                {config.mode === "live" ? t("integrations.live") : t("integrations.sandbox")}
              </Badge>
            ) : null}
          </div>
        </div>
        {provider.docs_url ? (
          <a href={provider.docs_url} target="_blank" rel="noreferrer" className="link">
            {t("integrations.docs")}
          </a>
        ) : null}
      </div>
      {/* Warning of the provider (e.g. what the real mode of Moneroo needs, D-128), translated by the API */}
      {provider.notice ? (
        <p role="note" className="notice-warning">
          {provider.notice}
        </p>
      ) : null}
      {/* D-128: the address a payment provider calls back (to paste in its dashboard) */}
      {config?.webhook_url ? (
        <div className="row">
          <span className="muted">{t("integrations.webhook")}</span>
          <code style={{ wordBreak: "break-all" }}>{config.webhook_url}</code>
          <Button variant="ghost" onClick={() => void navigator.clipboard?.writeText(config.webhook_url)}>
            {t("integrations.copy")}
          </Button>
        </div>
      ) : null}

      {config?.health ? <ProviderHealthRow config={config} onChanged={() => void refresh()} /> : null}

      <form onSubmit={onSubmit} className="stack">
        {provider.fields.map((field) => (
          <TextField
            key={field.name}
            label={field.label}
            type={field.secret ? "password" : "text"}
            autoComplete="off"
            placeholder={field.secret ? masked[field.name] ?? "" : settings[field.name] ?? ""}
            hint={
              field.secret && masked[field.name]
                ? `${t("integrations.secretSaved", { masked: masked[field.name] })} ${t("integrations.secretHint")}`
                : field.help_text || undefined
            }
            value={values[field.name] ?? ""}
            onChange={(e) => setValues((v) => ({ ...v, [field.name]: e.target.value }))}
          />
        ))}
        {config?.last_test_ok != null ? (
          <p role="status" className={config.last_test_ok ? "ok" : "ko"}>
            {/* The technical detail stays in the console: it is stored in one language only. */}
            {config.last_test_ok ? t("integrations.testOk") : t("integrations.testFailed")}
          </p>
        ) : null}
        <div className="row">
          <Button type="submit" disabled={saving}>
            {saving ? t("common.loading") : t("common.save")}
          </Button>
          {config ? (
            <Button variant="secondary" disabled={test.isPending} onClick={() => test.mutate({ id: config.id })}>
              {test.isPending ? t("integrations.testing") : t("integrations.test")}
            </Button>
          ) : null}
          {config && config.mode !== "live" && isOwner ? (
            <Button variant="ghost" onClick={() => setConfirmLive(true)}>
              {t("integrations.goLive")}
            </Button>
          ) : null}
        </div>
      </form>

      <GlassSheet
        open={confirmLive}
        title={t("integrations.goLiveTitle")}
        onClose={() => setConfirmLive(false)}
      >
        <p>{t("integrations.goLiveText")}</p>
        <div className="row">
          <Button variant="secondary" onClick={() => setConfirmLive(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={() =>
              config &&
              update.mutate(
                { id: config.id, data: { mode: "live", confirm_live: true } },
                { onSettled: () => setConfirmLive(false) },
              )
            }
          >
            {t("common.confirm")}
          </Button>
        </div>
      </GlassSheet>
    </GlassCard>
  );
}

export function IntegrationsPage() {
  const { t } = useTranslation();
  const providers = useIntegrationsProvidersList();
  const configs = useIntegrationsConfigsList();
  const role = useCurrentRole();

  const forbidden = [providers.error, configs.error].some(
    (e) => e instanceof ApiError && e.status === 403,
  );
  let content;
  if (forbidden) content = <GlassCard role="alert">{t("integrations.forbidden")}</GlassCard>;
  else if (providers.isPending || configs.isPending)
    content = (
      <GlassCard aria-busy="true">
        <Skeleton />
      </GlassCard>
    );
  else if (providers.data?.status !== 200 || configs.data?.status !== 200)
    content = <GlassCard role="alert">{t("common.error")}</GlassCard>;
  else {
    const byKey = new Map(
      configs.data.data.results.map((c) => [`${c.category}/${c.provider_key}`, c]),
    );
    content = (
      <div className="grid">
        {providers.data.data.map((provider) => (
          <ProviderCard
            key={`${provider.category}/${provider.key}`}
            provider={provider}
            config={byKey.get(`${provider.category}/${provider.key}`)}
            isOwner={role === "owner"}
          />
        ))}
      </div>
    );
  }

  return (
    <SettingsLayout title={t("integrations.title")} intro={t("integrations.intro")}>
      {content}
    </SettingsLayout>
  );
}
